import type {Item} from './infra';

export const inputTypes = [
  {value:'string', label:'텍스트'}, {value:'number', label:'숫자'},
  {value:'boolean', label:'사용 여부'}, {value:'enum', label:'선택 목록'},
  {value:'secret', label:'비밀값'},
];

/** Keep extension properties; reset only settings tied to the previous type. */
export function changeInputType(input: Item, type: string): Item {
  if ((input.type || 'string') === type) return input;
  const next = {...input, type};
  for (const key of ['default','generate','generateSecret','options','minimum','maximum','integer']) delete next[key];
  if (type === 'enum') next.options = [];
  return next;
}

export function inputDefinitionError(inputs: Item[], compose: string): {index:number; message:string} | null {
  const keys = new Set<string>();
  const used = new Set([...compose.matchAll(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*}}/g)].map(match => match[1]));
  for (let index=0; index<inputs.length; index++) {
    const input = inputs[index], type = input.type || 'string';
    const fail = (message:string) => ({index, message:`${input.label || input.key || '입력 항목 '+(index+1)}: ${message}`});
    if (!/^[a-zA-Z_][a-zA-Z0-9_]{0,63}$/.test(input.key || '')) return fail('식별자는 영문 또는 밑줄로 시작하는 영문·숫자·밑줄 1~64자로 입력하세요.');
    if (keys.has(input.key)) return fail('식별자가 중복되었습니다. 다른 식별자를 사용하세요.');
    keys.add(input.key);
    if (!inputTypes.some(option => option.value === type)) return fail('입력 유형을 선택하세요.');
    for (const key of ['minimum','maximum','minLength','maxLength']) {
      if (input[key] !== undefined && (typeof input[key] !== 'number' || !Number.isFinite(input[key]))) return fail('범위와 글자 수에는 숫자를 입력하거나 값을 비우세요.');
    }
    for (const key of ['minLength','maxLength']) {
      if (input[key] !== undefined && (!Number.isInteger(input[key]) || input[key]<0 || input[key]>4096)) return fail('글자 수는 0~4096 사이의 정수로 입력하세요.');
    }
    if (input.minLength !== undefined && input.maxLength !== undefined && input.minLength > input.maxLength) return fail('최소 글자 수는 최대 글자 수 이하여야 합니다.');
    if (input.minimum !== undefined && input.maximum !== undefined && input.minimum > input.maximum) return fail('최솟값은 최댓값 이하여야 합니다.');
    if (input.generate) {
      if (type !== 'number') return fail('범위 내 자동 생성은 숫자 유형에서 사용하세요.');
      if (input.minimum === undefined || input.maximum === undefined) return fail('자동 생성할 최솟값과 최댓값을 모두 입력하세요.');
      if (input.integer && Math.ceil(input.minimum)>Math.floor(input.maximum)) return fail('범위 안에 생성할 정수가 없습니다.');
    }
    if (input.generateSecret && type !== 'secret') return fail('비밀값 자동 생성은 비밀값 유형에서 사용하세요.');
    if (type === 'enum') {
      if (!Array.isArray(input.options) || !input.options.length || input.options.some((value:any) => value === null || !['string','number','boolean'].includes(typeof value) || !String(value).trim())) return fail('비어 있지 않은 선택지를 한 개 이상 추가하세요.');
      if (new Set(input.options.map(String)).size !== input.options.length) return fail('선택지가 중복되었습니다.');
    }
    if (type === 'secret' && Object.hasOwn(input,'default')) return fail('비밀값에는 기본값을 저장할 수 없습니다.');
    if (input.default !== undefined && input.default !== null) {
      const value = input.default, text = String(value), number = Number(value);
      if (!['string','number','boolean'].includes(typeof value)) return fail('기본값은 단일 값이어야 합니다.');
      if (type === 'number' && (!text.trim() || !Number.isFinite(number) || (input.integer && !Number.isInteger(number)) || (input.minimum !== undefined && number<input.minimum) || (input.maximum !== undefined && number>input.maximum))) return fail('기본값을 숫자 형식과 허용 범위에 맞게 입력하세요.');
      if (type === 'boolean' && !['true','false'].includes(text)) return fail('기본값에서 사용 또는 사용 안 함을 선택하세요.');
      if (type === 'enum' && !input.options.map(String).includes(text)) return fail('기본값을 현재 선택지 중에서 선택하세요.');
      if (Array.from(text).length < (input.minLength ?? 0) || Array.from(text).length > (input.maxLength ?? 4096)) return fail('기본값의 글자 수를 확인하세요.');
    }
  }
  for (let index=0; index<inputs.length; index++) {
    const input=inputs[index];
    if (!used.has(input.key)) return {index,message:`${input.label || input.key}: Docker Compose에 {{ ${input.key} }}를 사용하세요. 사용하지 않는 항목은 삭제할 수 있습니다.`};
  }
  // Java owns pattern validation and the final Compose/placeholder check.
  return null;
}
